import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { AppIcon, Badge, Button, Notice, Plate, SelectField, TabPanel, Tabs, TextField } from "@wilco/ui";
import { useState } from "react";
import { formatDate, formatMoney, formatNumber, t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { addToDraft, purchasingApi } from "./api.ts";
import { PaymentBadge } from "./badges.tsx";
import { adviceQuery, basketsQuery, expectedQuery, frequentQuery, orderQuery, ordersQuery, PURCHASING, suppliersQuery, tasksQuery } from "./queries.ts";
import type { Basket, BasketLine, Order } from "./types.ts";

const route = getRouteApi("/app/purchasing");

export type PurchasingTab = "shortage" | "expected" | "drafts";

interface ResultNotice extends NoticeState {
  order?: Order;
}

/**
 * «Закупівлі (робоче місце)» (chmk_office, purchasing): дефицит корзинами
 * по поставщику, ожидаемые поставки, черновики. Итог действия над
 * списком, а не диалогом: закупщик заказывает несколько корзин подряд, и
 * черновик сам не открывается.
 */
export function PurchasingPage() {
  const { tab = "shortage" } = route.useSearch();
  const navigate = useNavigate();
  const baskets = useQuery(basketsQuery);
  const expected = useQuery(expectedQuery);
  const drafts = useQuery(ordersQuery(["DRAFTS"]));
  const [notice, setNotice] = useState<ResultNotice | null>(null);

  const shortageCount = baskets.data?.reduce((a, b) => a + b.lines.length, 0);
  const late = expected.data?.some((e) => e.overdueDays > 0) ?? false;

  return (
    <div className="page purchasing">
      <h1 className="page-title page-title-icon">
        <AppIcon name="purchase" size={32} />
        {t("purchasing.title")}
      </h1>
      <Tasks />
      {notice ? (
        <Notice tone={notice.tone} title={notice.title} className="stock-notice">
          {notice.body}
          {notice.order ? (
            <Link to="/purchasing/orders/$id" params={{ id: notice.order.id }} className="notice-link">
              {t("purchasing.openDraft", { number: notice.order.number, supplier: notice.order.supplierName })}
            </Link>
          ) : null}
        </Notice>
      ) : null}
      <Tabs<PurchasingTab>
        value={tab}
        onValueChange={(v) => {
          setNotice(null);
          void navigate({ to: "/purchasing", search: { tab: v }, replace: true });
        }}
        label={t("purchasing.tabs")}
        items={[
          { value: "shortage", label: t("purchasing.tab.shortage"), count: shortageCount },
          { value: "expected", label: t("purchasing.tab.expected"), count: expected.data?.length, alert: late },
          { value: "drafts", label: t("purchasing.tab.drafts"), count: drafts.data?.length },
        ]}
      >
        <TabPanel value="shortage" className="queue">
          {baskets.data?.length === 0 ? <Empty title={t("purchasing.shortage.empty")} lead={t("purchasing.shortage.emptyLead")} /> : null}
          {baskets.data?.map((b) => (
            <BasketCard key={b.supplierId ?? "none"} basket={b} onNotice={setNotice} />
          ))}
          <FrequentList onNotice={setNotice} />
        </TabPanel>
        <TabPanel value="expected" className="queue">
          {expected.data?.length === 0 ? <Empty title={t("purchasing.expected.empty")} lead={t("purchasing.expected.emptyLead")} /> : null}
          {expected.data?.map((e) => (
            <Link key={e.orderId} to="/purchasing/orders/$id" params={{ id: e.orderId }} className="order-row" data-late={e.overdueDays > 0 ? "" : undefined}>
              <span className="order-row-main">
                <span className="w-num queue-doc">{e.number}</span>
                <span className="queue-name">{e.supplierName}</span>
              </span>
              <span className="order-row-meta">
                {e.overdueDays > 0 ? (
                  <Badge tone="danger">{t("purchasing.overdue", { days: e.overdueDays })}</Badge>
                ) : (
                  <span className="w-muted">{formatDate(e.expectedArrival.slice(0, 10))}</span>
                )}
                <span className="w-muted">{t("purchasing.received", { percent: formatNumber(e.receivedPercent) })}</span>
                <PaymentBadge status={e.paymentStatus} />
                <span className="w-num order-row-sum">{formatMoney(e.gross, e.currency)}</span>
              </span>
            </Link>
          ))}
        </TabPanel>
        <TabPanel value="drafts" className="queue">
          {drafts.data?.length === 0 ? <Empty title={t("purchasing.drafts.empty")} lead={t("purchasing.drafts.emptyLead")} /> : null}
          {drafts.data?.map((o) => (
            <Link key={o.id} to="/purchasing/orders/$id" params={{ id: o.id }} className="order-row">
              <span className="order-row-main">
                <span className="w-num queue-doc">{o.number}</span>
                <span className="queue-name">{o.supplierName}</span>
              </span>
              <span className="order-row-meta">
                <Badge tone={o.state === "AWAITING_APPROVAL" ? "warning" : "neutral"}>{t(o.state === "AWAITING_APPROVAL" ? "order.state.AWAITING_APPROVAL" : "order.state.DRAFT")}</Badge>
                <span className="w-num order-row-sum">{formatMoney(o.gross, o.currency)}</span>
              </span>
            </Link>
          ))}
        </TabPanel>
      </Tabs>
    </div>
  );
}

function Empty({ title, lead }: { title: string; lead: string }) {
  return (
    <div className="queue-empty">
      <p className="queue-empty-title">{title}</p>
      <p className="w-muted">{lead}</p>
    </div>
  );
}

/** H7, ЗК-19: «Звірити ціну з рахунком і внести оплату» через 3 дня после приёмки. */
function Tasks() {
  const tasks = useQuery(tasksQuery);
  const orders = useQueries({ queries: (tasks.data ?? []).map((id) => orderQuery(id)) });
  const loaded = orders.map((o) => o.data).filter((o): o is Order => o !== undefined);
  if (loaded.length === 0) return null;
  return (
    <Notice tone="warning" title={t("purchasing.task.title")} className="stock-notice">
      <ul className="task-list">
        {loaded.map((o) => (
          <li key={o.id}>
            <Link to="/purchasing/orders/$id" params={{ id: o.id }} className="notice-link">
              {`${o.number} · ${o.supplierName}`}
            </Link>
            {` · ${[o.pricesConfirmed ? null : t("purchasing.task.price"), o.paymentStatus === "PAID" ? null : t("purchasing.task.payment")].filter(Boolean).join(", ")}`}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

const lineKey = (l: BasketLine) => `${l.productId}:${l.destination}`;

function BasketCard({ basket, onNotice }: { basket: Basket; onNotice: (n: ResultNotice | null) => void }) {
  const queryClient = useQueryClient();
  const [qty, setQty] = useState<Record<string, string>>({});
  const order = useMutation({ mutationFn: purchasingApi.orderBasket });
  const known = basket.lines.reduce((a, l) => a + (l.amount ?? 0), 0);
  const estimate = basket.lines.some((l) => l.amount === null);
  const noSupplier = basket.supplierId === null;

  const submit = () => {
    onNotice(null);
    const quantities = [];
    for (const l of basket.lines) {
      const raw = qty[lineKey(l)];
      if (raw === undefined) continue;
      const n = parseQty(raw);
      if (n === null) {
        onNotice({ tone: "danger", title: t("purchasing.qtyNotNumber", { product: l.productName }) });
        return;
      }
      quantities.push({ productId: l.productId, destination: l.destination, qty: n });
    }
    order.mutate(
      { supplierId: basket.supplierId, quantities },
      {
        onSuccess: (o) => {
          setQty({});
          onNotice({ tone: "success", title: t("purchasing.drafted"), order: o });
          void queryClient.invalidateQueries({ queryKey: PURCHASING });
        },
        // Введённое остаётся: при ошибке закупщик правит строку, а не набирает заново.
        onError: (e) => onNotice(errorNotice(e)),
      },
    );
  };

  return (
    <Plate cut="sm" bleed className="basket">
      <div className="basket-head">
        <h2 className="basket-title">{basket.supplierName ?? t("purchasing.noSupplier")}</h2>
        {!noSupplier ? (
          <span className="w-num basket-sum" title={estimate ? t("purchasing.estimateHint") : undefined}>
            {estimate ? `≈ ${formatMoney(known, "UAH")}` : formatMoney(known, "UAH")}
          </span>
        ) : null}
        {!noSupplier ? (
          <Button variant="create" disabled={order.isPending} onClick={submit}>
            {t("purchasing.order")}
          </Button>
        ) : null}
      </div>
      {noSupplier ? <p className="w-muted">{t("purchasing.noSupplierLead")}</p> : null}
      <ul className="basket-lines">
        {basket.lines.map((l) => (
          <li key={lineKey(l)} className="basket-line">
            <span className="basket-what">
              <span>
                <span className="w-num shelf-sku">{l.productSku}</span> {l.productName}
              </span>
              <span className="w-muted basket-for">{forText(l)}</span>
              <AdviceLine line={l} onUse={noSupplier ? undefined : (n) => setQty((p) => ({ ...p, [lineKey(l)]: String(n) }))} />
            </span>
            <span className="basket-qty">
              <TextField
                label={t("purchasing.qtyFor", { product: l.productName })}
                hideLabel
                inputMode="decimal"
                autoComplete="off"
                value={qty[lineKey(l)] ?? String(l.qty)}
                readOnly={noSupplier}
                onChange={(e) => setQty((p) => ({ ...p, [lineKey(l)]: e.target.value }))}
              />
              <span className="qty-uom">{l.uom}</span>
            </span>
            <span className="w-num basket-amount">{l.amount === null ? (noSupplier ? "" : "≈") : formatMoney(l.amount, "UAH")}</span>
            {noSupplier ? <AssignSupplier line={l} onNotice={onNotice} /> : null}
          </li>
        ))}
      </ul>
    </Plate>
  );
}

/** «для MO-0043 · Серія · до 10.11»: под что нужен товар. */
function forText(l: BasketLine): string {
  const parts = [l.orders.length ? t("purchasing.for", { orders: l.orders.join(", ") }) : null, l.destinationName, l.needDate ? t("purchasing.until", { date: formatDate(l.needDate) }) : null];
  return parts.filter(Boolean).join(" · ");
}

/** Подсказка дозаказа строки (ЗК-05..ЗК-07): нулевой расход это «немає даних». */
function AdviceLine({ line, onUse }: { line: BasketLine; onUse: ((qty: number) => void) | undefined }) {
  const advice = useQuery(adviceQuery(line.destination, line.productId));
  const a = advice.data;
  if (!a) return null;
  if (a.noData) return <span className="advice w-muted">{t("purchasing.advice.noData")}</span>;
  const parts: string[] = [
    a.dailyUse === null ? null : t("draft.use", { qty: formatNumber(a.dailyUse), uom: line.uom }),
    a.daysOfStock === null ? null : t("purchasing.advice.days", { days: formatNumber(a.daysOfStock) }),
    a.belowMinimum ? t("purchasing.advice.belowMin") : null,
  ].filter((x): x is string => x !== null);
  return (
    <span className="advice">
      <span className="w-muted">{parts.join(" · ")}</span>
      {a.missingSupplierData ? <span className="advice-warn">{t("purchasing.advice.missing")}</span> : null}
      {a.qty > 0 && onUse ? (
        <button type="button" className="advice-use" onClick={() => onUse(a.qty)}>
          {t("purchasing.advice.use", { qty: formatNumber(a.qty), uom: line.uom })}
        </button>
      ) : null}
    </span>
  );
}

/** ЗК-03: поставщик в строке «Без постачальника», только компании. */
function AssignSupplier({ line, onNotice }: { line: BasketLine; onNotice: (n: ResultNotice | null) => void }) {
  const queryClient = useQueryClient();
  const suppliers = useQuery(suppliersQuery);
  const [supplierId, setSupplierId] = useState("");
  const assign = useMutation({ mutationFn: () => purchasingApi.assignSupplier(line.productId, supplierId) });
  const companies = (suppliers.data ?? []).filter((s) => s.kind === "COMPANY");
  return (
    <span className="basket-assign">
      <SelectField
        label={t("purchasing.supplier")}
        value={supplierId}
        onChange={(e) => setSupplierId(e.target.value)}
        options={[{ value: "", label: t("purchasing.chooseSupplier") }, ...companies.map((s) => ({ value: s.id, label: s.name }))]}
      />
      <Button
        disabled={!supplierId || assign.isPending}
        onClick={() =>
          assign.mutate(undefined, {
            onSuccess: () => {
              const name = companies.find((s) => s.id === supplierId)?.name ?? "";
              onNotice({ tone: "success", title: t("purchasing.assigned", { supplier: name, product: line.productName }) });
              void queryClient.invalidateQueries({ queryKey: PURCHASING });
            },
            onError: (e) => onNotice(errorNotice(e)),
          })
        }
      >
        {t("purchasing.assign")}
      </Button>
    </span>
  );
}

/** «Часто замовляєте»: прошлое количество одним нажатием в черновик поставщика. */
function FrequentList({ onNotice }: { onNotice: (n: ResultNotice | null) => void }) {
  const queryClient = useQueryClient();
  const frequent = useQuery(frequentQuery);
  const add = useMutation({ mutationFn: addToDraft });
  if (!frequent.data?.length) return null;
  return (
    <section className="stock-section" aria-labelledby="purchasing-frequent">
      <h2 id="purchasing-frequent" className="desk-group-title">
        {t("draft.frequent")}
      </h2>
      <ul className="hint-list">
        {frequent.data.map((f) => (
          <li key={f.productId} className="hint">
            <span className="hint-name">
              <span className="w-num">{f.productSku}</span> {f.productName}
            </span>
            <span className="hint-why w-muted">
              {t("draft.frequentWhy", { times: f.times, qty: formatNumber(f.lastQty), uom: f.uom })}
              {f.supplierName ? ` · ${f.supplierName}` : ""}
            </span>
            <Button
              className="hint-add"
              disabled={!f.supplierId || add.isPending}
              onClick={() => {
                if (!f.supplierId) return;
                onNotice(null);
                add.mutate(
                  { supplierId: f.supplierId, productId: f.productId, qty: f.lastQty },
                  {
                    onSuccess: (o) => {
                      onNotice({ tone: "success", title: t("purchasing.frequentAdded", { product: f.productName }), order: o });
                      void queryClient.invalidateQueries({ queryKey: PURCHASING });
                    },
                    onError: (e) => onNotice(errorNotice(e)),
                  },
                );
              }}
            >
              {`+\u00a0${formatNumber(f.lastQty)}\u00a0${f.uom}`}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
